import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Copy, Check, Flame, Share2, Clock, Zap, Target, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import BottomNav from '../components/BottomNav';

const APP_NAME = 'LocalFlame';
const APP_URL = 'https://localflame4life.com';

export default function Marketing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [copiedId, setCopiedId] = useState(null);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const CopyButton = ({ text, id }) => (
    <button
      data-testid={`copy-${id}`}
      onClick={() => copyToClipboard(text, id)}
      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs transition-colors flex-shrink-0"
    >
      {copiedId === id ? (
        <><Check className="w-3 h-3 text-green-500" /> Copied!</>
      ) : (
        <><Copy className="w-3 h-3" /> Copy</>
      )}
    </button>
  );

  const launchSchedule = [
    { time: '9:00 AM', platform: 'Reddit', action: 'Post to r/dating, r/Tinder, r/DatingApps' },
    { time: '9:30 AM', platform: 'Product Hunt', action: 'Submit launch post' },
    { time: '10:00 AM', platform: 'Hacker News', action: 'Submit Show HN post' },
    { time: '10:30 AM', platform: 'Twitter/X', action: 'Post launch thread (4 tweets)' },
    { time: '11:00 AM', platform: 'Instagram', action: 'Post carousel + first Story series' },
    { time: '11:30 AM', platform: 'TikTok', action: 'Post "The Math" video' },
    { time: '12:00 PM', platform: 'Facebook', action: 'Post in dating groups + local singles groups' },
    { time: '12:30 PM', platform: 'Reddit', action: 'Post to r/SideProject, r/startups, r/IndieHackers' },
    { time: '1:00 PM', platform: 'LinkedIn', action: 'Post founder story' },
    { time: '2:00 PM', platform: 'Craigslist', action: 'Post in top 10 US cities' },
    { time: '3:00 PM', platform: 'Twitter/X', action: 'Post second wave tweet' },
    { time: '4:00 PM', platform: 'Nextdoor', action: 'Post in local neighborhoods' },
    { time: '5:00 PM', platform: 'Reddit', action: 'Post to local city subreddits (top 20 cities)' },
    { time: '7:00 PM', platform: 'TikTok', action: 'Post "POV" video (prime time)' },
    { time: '8:00 PM', platform: 'Instagram', action: 'Post second Story series + Reel' },
    { time: '9:00 PM', platform: 'Twitter/X', action: 'Post evening engagement tweet' },
  ];

  const platforms = [
    {
      id: 'schedule',
      name: 'Launch Day',
      icon: '🚀',
    },
    {
      id: 'reddit',
      name: 'Reddit',
      icon: '🔴',
      subreddits: [
        '**Dating subreddits:** r/dating, r/Tinder, r/OnlineDating, r/DatingApps, r/hookup, r/r4r, r/singles, r/dating_advice, r/Bumble',
        '**Startup subreddits:** r/SideProject, r/startups, r/IndieHackers, r/AlphaAndBetaUsers, r/RoastMyStartup, r/EntrepreneurRideAlong',
        '**Top 20 city subreddits:** r/nyc, r/LosAngeles, r/chicago, r/houston, r/phoenix, r/philadelphia, r/sanantonio, r/sandiego, r/dallas, r/austin, r/jacksonville, r/miami, r/atlanta, r/seattle, r/denver, r/nashville, r/portland, r/lasvegas, r/boston, r/charlotte'
      ],
      posts: [
        {
          title: 'Tired of paying $30/month for dating apps? I built one with a $19.99 one-time fee',
          body: `I got frustrated paying monthly subscriptions for dating apps that keep paywalling basic features. So I built ${APP_NAME} — a hookup/dating app where you pay once ($19.99) and get EVERYTHING unlocked forever.

What makes it different:
- Local matches only (25 mile radius) — no matching with people in another state
- Unlimited swipes, full messaging, video calls
- No monthly fees, no "premium" tiers, no paying to see who liked you
- Block/report system for safety
- Profile verification to reduce catfishing

The thesis: dating apps profit from keeping you single and subscribed. ${APP_NAME} profits when you sign up. After that, we genuinely want you to find your match ASAP.

${APP_URL}

What do you think about the one-time payment model for dating?`
        },
        {
          title: `[Launch] ${APP_NAME} — The dating app that wants you to DELETE it`,
          body: `Just launched ${APP_NAME} and the whole point is that we WANT you to find someone and stop using the app.

Why? Because we charge $19.99 once. Not monthly. Once.

That means our incentive is to get you matched, not to keep you swiping forever.

Features:
- Only shows people within 25 miles
- Unlimited everything (swipes, messages, video calls)
- Verified profiles
- No ads, no subscriptions, no tricks

${APP_URL}

I built this because I spent $1,200+ on dating apps last year and the model is fundamentally broken. Thoughts?`
        },
        {
          title: '[City] New dating app for locals only — $19.99 one time, no subscriptions',
          body: `Hey [city] people! I'm launching ${APP_NAME}, a dating app that only matches you with people within 25 miles.

It costs $19.99 once (NOT monthly) and every feature is unlocked forever — swipes, messaging, video calls, the works.

I'm specifically targeting local communities because long-distance matching is pointless for hookups/casual dating. If you're not within driving distance, what's the point?

Check it out: ${APP_URL}

Looking for early users in [city] to build the local community. Would love feedback!`
        }
      ]
    },
    {
      id: 'twitter',
      name: 'Twitter/X',
      icon: '🐦',
      posts: [
        {
          title: 'Launch Thread (Tweet 1 of 4)',
          body: `I just launched a dating app that wants you to DELETE it.

Here's why 🧵`
        },
        {
          title: 'Thread Tweet 2',
          body: `Dating apps make $6 BILLION a year.

Their business model? Charge you $30/month. Limit free features. Keep you swiping.

They literally profit from you being single.

The longer you're single, the more you pay.`
        },
        {
          title: 'Thread Tweet 3',
          body: `${APP_NAME} flips this:

• $19.99 once. Forever.
• Every feature unlocked
• Only local matches (25 miles)
• Video calls, messaging, verified profiles
• No ads. No subscriptions. No games.

We make money when you sign up.
After that? We want you OFF the app.`
        },
        {
          title: 'Thread Tweet 4',
          body: `If a dating app profits when you stay single, it will ALWAYS work against you.

${APP_NAME} is the alternative.

${APP_URL}

$19.99. Once. Go find your person. Then delete us.`
        },
        {
          title: 'Standalone Tweet 1',
          body: `POV: You just paid $19.99 and never have to pay for a dating app again.

No monthly fees.
No "premium" unlock.
No paying to see your likes.

Just local matches within 25 miles.

${APP_NAME} → ${APP_URL}`
        },
        {
          title: 'Standalone Tweet 2',
          body: `Math:

Tinder Gold: $360/year
Bumble Premium: $480/year  
Hinge Preferred: $420/year

${APP_NAME}: $19.99/forever

Your move.

${APP_URL}`
        },
        {
          title: 'Evening Engagement Tweet',
          body: `Honest question: Would you switch to a dating app with a one-time fee instead of monthly subscriptions?

I built one. $19.99, all features, local matches only.

${APP_URL}

What's stopping you?`
        }
      ]
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      icon: '🎵',
      posts: [
        {
          title: 'Video 1 — "The Math" (Post at 11:30 AM)',
          body: `[HOOK - text on screen]: "I did the math on dating apps..."

[SCRIPT]:
"I added up how much I spent on dating apps last year. Tinder Gold — $30 a month. Bumble Premium — $40 a month. Hinge Preferred — $35 a month. That's over $1,200 a year. To MAYBE get a date.

So I built ${APP_NAME}. $19.99. One time. Every feature unlocked forever. Only local matches within 25 miles. No subscriptions. Ever.

Link in bio. Stop renting your love life."

[CAPTION]: I did the math and I'm never paying a dating app subscription again 💀

[HASHTAGS]: #datingapp #tinder #bumble #hinge #dating #hookup #localflame #nosub #singletok #datingadvice #datingin2025 #relationships #fyp

[SOUND]: Use trending audio or original voiceover`
        },
        {
          title: 'Video 2 — "POV" (Post at 7:00 PM - PRIME TIME)',
          body: `[HOOK - text on screen]: "POV: A dating app that actually WANTS you to find someone"

[SCRIPT]:
"This is ${APP_NAME}. It costs $19.99. One time. You get everything — unlimited swipes, messaging, video calls. And it only shows you people within 25 miles.

Why? Because most dating apps charge monthly and profit from keeping you single. We charge once and want you OFF the app as fast as possible.

That's it. That's the whole pitch."

[CAPTION]: Why would a dating app want you to be single forever? Because subscriptions 🤑 ${APP_NAME} is different.

[HASHTAGS]: #datingapps #swipeleft #dating2025 #hookupculture #localflame #singletok #pov #fyp #datingtips #relationships

[SOUND]: Trending audio or "Oh No" sound`
        },
        {
          title: 'Video 3 — "Storytime / Rant" (Post Day 2)',
          body: `[HOOK]: "I got scammed by dating apps for 3 years"

[SCRIPT]:
"Every single month, Tinder would show me a blurred photo of someone who 'liked me' and tell me to pay $30 to see who it was. Then Bumble wanted $40 a month to let me re-match with people. And Hinge literally charges you to see your own likes.

So I built my own. ${APP_NAME}. $19.99 once. See everything. Match with everyone. Only local people. No monthly fees. No tricks.

Would you try it? Link in bio."

[HASHTAGS]: #storytime #datingapp #tinder #bumble #hinge #scam #localflame #onetimepayment #fyp`
        }
      ]
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: '📸',
      posts: [
        {
          title: 'Launch Post / Carousel Caption',
          body: `Stop paying rent on your love life. 🔥

${APP_NAME} is a dating app with ONE rule: pay $19.99 once, get everything forever.

What you get:
✅ Unlimited swipes
✅ See who likes you
✅ Messaging & video calls
✅ Verified profiles
✅ Local matches only (25mi)

What you DON'T get:
❌ Monthly fees
❌ "Premium" paywalls
❌ Matches 500 miles away
❌ Ads

Link in bio → ${APP_URL}

#dating #datingapp #hookup #tinder #bumble #hinge #localflame #nosub #onetimefee #single #matches #lovelocal #datingin2025 #singletok #relationships #love`
        },
        {
          title: 'Story Series (5 slides) — Post at 11 AM',
          body: `Slide 1: "How much have you spent on dating apps? 💸" [Add poll: Under $100 / Over $500]

Slide 2: "Average dating app user spends $240/year on subscriptions"

Slide 3: "${APP_NAME}: $19.99. Total. Forever. 🔥" [Big bold text]

Slide 4: "Local only. 25 mile radius. Real people near you." [Add location sticker]

Slide 5: "Link in bio → Stop subscribing. Start matching." [Add link sticker to ${APP_URL}]`
        },
        {
          title: 'Reel Script — Post at 8 PM',
          body: `[Format: Screen recording of the app with voiceover]

"Okay so this is ${APP_NAME} and it costs $19.99 one time. Not monthly. ONE TIME.

[Show swipe screen] You get unlimited swipes.
[Show matches] You see all your matches.
[Show chat] Full messaging.
[Show video call] Video calls.

And it ONLY shows you people within 25 miles because what's the point of matching with someone in another state?

No subscriptions. No premium tier. No bs.

Link in bio."

[CAPTION]: The dating app that doesn't want your monthly subscription 💀 #datingapp #localflame #fyp`
        },
        {
          title: 'Bio Template',
          body: `${APP_NAME} 🔥
The dating app you pay for ONCE
$19.99 → Every feature. Forever.
Local matches only (25mi)
No subscriptions. No tricks.
👇 Download now
${APP_URL}`
        }
      ]
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: '📘',
      groups: [
        'Search "singles" + your target city (e.g., "Singles in Miami")',
        'Search "dating" + your area',
        '"Young professionals" groups in major cities',
        '"Side project" and "startup" groups',
        '"New in [city]" groups — newcomers are prime dating app users'
      ],
      posts: [
        {
          title: 'Dating/Singles Group Post',
          body: `Hey everyone! Just launched something I think this group will appreciate.

${APP_NAME} is a dating app built on a simple idea: you should only pay ONCE.

💰 $19.99 one-time fee — every feature unlocked forever
📍 Only matches within 25 miles — real local connections  
💬 Unlimited messaging & video calls
🔒 Verified profiles & block/report safety features
🚫 No monthly subscriptions, no ads, no tricks

I built this because dating apps are broken — they profit from keeping you single and subscribed. ${APP_NAME} makes money when you sign up, so we actually WANT you to find someone.

${APP_URL}

Would love to hear what you think! 🙌`
        },
        {
          title: 'Facebook Marketplace Post (Free listing)',
          body: `${APP_NAME} — Local Dating App | $19.99 One-Time

Tired of $30/month dating app subscriptions?

${APP_NAME} charges $19.99 once. Forever.

All features:
- Unlimited swipes
- Messaging & video calls
- Local matches only (25 miles)
- Verified profiles

No subscriptions. No hidden costs.

${APP_URL}`
        }
      ]
    },
    {
      id: 'producthunt',
      name: 'Product Hunt',
      icon: '🚀',
      posts: [
        {
          title: `${APP_NAME} — The dating app that wants you to delete it`,
          body: `TAGLINE: "Pay once. Find your match. Delete us."

DESCRIPTION:
${APP_NAME} is a dating/hookup app built on a radical idea: pay once, get everything.

For $19.99 (one-time), you unlock:
🔥 Unlimited swipes
💬 Full messaging  
📹 Video calls
📍 Local matches only (25-mile radius)
✅ Verified profiles
🛡️ Block/report safety features

No monthly fees. No "premium" tiers. No matching with people 500 miles away.

The subscription model in dating apps is fundamentally broken — these companies profit when you stay single. We profit when you sign up. After that, we genuinely want you to find your match and delete the app.

FIRST COMMENT (as maker):
"I built ${APP_NAME} because I spent over $1,200 on dating app subscriptions last year and realized the game is rigged. These apps are designed to keep you swiping, not to help you find someone.

The one-time fee model aligns our incentives with yours: we make money upfront, so we have every reason to help you match quickly. The faster you find someone, the more likely you'll recommend us.

Would love your feedback on the concept and the product. AMA!"

TOPICS TO TAG: Dating, Social Networking, Marketplace

HUNTER NOTES: Submit between 12:01 AM - 1:00 AM PT (Tuesday or Wednesday for best visibility)`
        }
      ]
    },
    {
      id: 'hackernews',
      name: 'Hacker News',
      icon: '🟧',
      posts: [
        {
          title: `Show HN: ${APP_NAME} – Dating app with one-time fee, no subscriptions`,
          body: `I built a dating/hookup app with a different business model.

Most dating apps charge $20-40/month and deliberately limit free features to drive upgrades. Their incentive structure keeps users single and subscribed.

${APP_NAME} charges $19.99 once. Everything included:
- Swipe-based matching with configurable distance/age/gender filters
- Only local matches (haversine distance, max 25mi default, adjustable to 50mi)
- Full messaging, video calls (WebRTC + STUN)
- Profile verification, block/report system
- No ads, no subscriptions

Tech stack: React (Framer Motion for swipe physics) + FastAPI + MongoDB + Stripe. WebRTC with STUN for video. JWT auth with 24h tokens.

Business thesis: if your dating app's revenue depends on retention (subscriptions), the product is incentivized to work AGAINST the user. A one-time fee aligns incentives — we want users to match and leave happy.

${APP_URL}

Feedback welcome. Especially interested in thoughts on the business model viability.`
        }
      ]
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: '💼',
      posts: [
        {
          title: 'Founder Launch Post',
          body: `I just launched a dating app. And I want users to delete it.

Here's the story:

Last year, I spent $1,200 on dating app subscriptions. Tinder, Bumble, Hinge — the whole stack.

Then I realized something: these apps are literally designed to keep me single. Their revenue model depends on monthly retention. The longer I'm single, the more I pay.

So I built ${APP_NAME}.

It costs $19.99. Once. Forever. Every feature unlocked.

The model is simple: we make money when you sign up. After that, we want you to find your match as fast as possible. Happy users recommend us. Unhappy subscribers don't.

Features:
→ Only local matches (25-mile radius)
→ Unlimited swipes, messaging, video calls
→ Verified profiles
→ No ads, no premium tier, no tricks

We're live now: ${APP_URL}

I'm not claiming this will replace Tinder. But I think there's a market for people who are tired of the subscription treadmill.

What do you think — is the one-time payment model viable for dating apps?

#startup #dating #productlaunch #buildinpublic #entrepreneurship`
        }
      ]
    },
    {
      id: 'craigslist',
      name: 'Craigslist',
      icon: '📋',
      subreddits: [
        'Post in Community > Activities section in these cities:',
        'New York, Los Angeles, Chicago, Houston, Phoenix, Philadelphia, San Antonio, San Diego, Dallas, Austin, Miami, Atlanta, Seattle, Denver, Nashville, Portland, Las Vegas, Boston, Charlotte, Minneapolis'
      ],
      posts: [
        {
          title: 'Craigslist Post (Community > Activities)',
          body: `Tired of paying $30/month for dating apps?

${APP_NAME} — the local dating app with a one-time $19.99 fee.

✓ All features included: swiping, messaging, video calls
✓ Only matches people within 25 miles of you
✓ Verified profiles for safety
✓ No subscriptions. No hidden costs. Ever.

${APP_URL}

Pay once. Date forever.`
        }
      ]
    },
    {
      id: 'other',
      name: 'More Platforms',
      icon: '🌐',
      platforms: [
        { name: 'Nextdoor', note: 'Post in your neighborhood. Perfect for "local only" angle. Post at 4 PM when people check after work.' },
        { name: 'IndieHackers.com', note: 'Post in "Show" section. Great community for founder-led products. Include revenue/user numbers.' },
        { name: 'BetaList.com', note: 'Submit for free at betalist.com/submit. Gets early adopters. Takes 1-2 weeks to approve.' },
        { name: 'AlternativeTo.net', note: 'Add as alternative to Tinder, Bumble, Hinge, OkCupid. Free and drives SEO traffic.' },
        { name: 'Quora', note: 'Answer questions like "What are the best dating apps?" and "Are dating apps worth paying for?" Mention LocalFlame.' },
        { name: 'Medium', note: 'Write "Why I Built a Dating App That Wants You to Delete It" — great for SEO and credibility.' },
        { name: 'Threads', note: 'Repost your Twitter/X content. Same audience, less competition.' },
        { name: 'Snapchat Spotlight', note: 'Repost TikTok videos here. Spotlight pays creators for views.' },
        { name: 'Pinterest', note: 'Create infographic pins: "Dating App Cost Comparison" with LocalFlame as the cheapest option.' },
        { name: 'YouTube Shorts', note: 'Repost TikTok videos as Shorts. Same content, new audience.' },
        { name: 'Discord', note: 'Join dating/singles Discord servers and share in #introductions or #app-recommendations channels.' },
        { name: 'Google Business Profile', note: 'Create a local business listing if targeting specific cities. Helps with local SEO.' },
        { name: 'Yelp', note: 'Create a listing under "Social Clubs" or "Dating Services" in target cities.' },
      ]
    }
  ];

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-6">
        <p className="text-zinc-400">Admin access required</p>
        <Button onClick={() => navigate('/app')} variant="outline" className="mt-4 border-zinc-700 text-zinc-300">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      {/* Header */}
      <header className="px-6 py-6 border-b border-zinc-900">
        <div className="flex items-center gap-3 mb-2">
          <button data-testid="back-to-profile" onClick={() => navigate('/app/profile')} className="w-10 h-10 rounded-full bg-zinc-900 flex items-center justify-center text-zinc-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-outfit text-2xl font-semibold text-white flex items-center gap-2">
              <Share2 className="w-6 h-6 text-rose-500" />
              Launch Day Marketing
            </h1>
            <p className="text-zinc-500 text-sm mt-0.5">Copy-paste content for every platform — post TODAY</p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 p-3 rounded-xl bg-zinc-900 border border-zinc-800">
          <span className="text-zinc-400 text-sm flex-1 truncate">{APP_URL}</span>
          <CopyButton text={APP_URL} id="app-url" />
        </div>
      </header>

      <main className="px-6 py-6">
        <Tabs defaultValue="schedule" className="w-full">
          <TabsList className="w-full flex-wrap h-auto gap-1 bg-zinc-900/50 p-1.5 rounded-xl mb-6">
            {platforms.map((p) => (
              <TabsTrigger key={p.id} value={p.id} data-testid={`tab-${p.id}`} className="text-xs px-3 py-2 data-[state=active]:bg-rose-600 data-[state=active]:text-white rounded-lg">
                {p.icon} {p.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Launch Schedule Tab */}
          <TabsContent value="schedule" className="space-y-6">
            <div className="p-5 rounded-xl bg-gradient-to-br from-rose-600/10 to-zinc-900/50 border border-rose-500/20">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-5 h-5 text-rose-500" />
                <h3 className="font-outfit font-semibold text-white">Launch Day Posting Schedule</h3>
              </div>
              <p className="text-zinc-400 text-sm mb-4">Follow this timeline for maximum impact. All times are your local time.</p>
              <div className="space-y-2">
                {launchSchedule.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 py-2 border-b border-zinc-800/50 last:border-0">
                    <span className="text-rose-500 font-mono text-sm w-20 flex-shrink-0 pt-0.5">{item.time}</span>
                    <span className="text-zinc-500 text-sm w-20 flex-shrink-0">{item.platform}</span>
                    <span className="text-zinc-300 text-sm">{item.action}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick account setup checklist */}
            <div className="p-5 rounded-xl bg-zinc-900/50 border border-zinc-800">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-5 h-5 text-rose-500" />
                <h3 className="font-outfit font-semibold text-white">Account Setup Checklist</h3>
              </div>
              <div className="space-y-2 text-zinc-300 text-sm">
                {[
                  'Create @LocalFlame accounts on Twitter/X, Instagram, TikTok, Reddit',
                  'Set profile photo to the app icon/flame logo on all platforms',
                  'Add bio with app URL on all platforms',
                  'Create a Reddit account (if new, engage for a few hours before posting to avoid spam filters)',
                  'Create a Product Hunt maker profile',
                  'Sign up for Hacker News if you don\'t have an account',
                  'Join 10+ Facebook dating/singles groups in major US cities',
                  'Install Nextdoor and verify your neighborhood',
                  'Create Craigslist accounts for top 10 target cities',
                  'Prepare 2-3 short TikTok videos (screen recordings of the app work great)',
                ].map((item, i) => (
                  <label key={i} className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" className="mt-1 accent-rose-500" />
                    <span>{item}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Pro tips */}
            <div className="p-5 rounded-xl bg-zinc-900/50 border border-zinc-800">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-rose-500" />
                <h3 className="font-outfit font-semibold text-white">Pro Tips for Maximum Reach</h3>
              </div>
              <ul className="space-y-2 text-zinc-300 text-sm">
                <li className="flex items-start gap-2"><span className="text-rose-500 font-bold">1.</span> Reddit drives the most organic traffic for app launches. Post to dating subs first, then startup subs.</li>
                <li className="flex items-start gap-2"><span className="text-rose-500 font-bold">2.</span> On Reddit, engage in comments genuinely. Answer questions. Don't just post and leave.</li>
                <li className="flex items-start gap-2"><span className="text-rose-500 font-bold">3.</span> TikTok: 15-30 second videos about "how much dating apps cost" have viral potential. Post at 7 PM for prime time.</li>
                <li className="flex items-start gap-2"><span className="text-rose-500 font-bold">4.</span> Your #1 hook: "No subscriptions." It's the biggest pain point for dating app users.</li>
                <li className="flex items-start gap-2"><span className="text-rose-500 font-bold">5.</span> Submit to Product Hunt on a Tuesday or Wednesday for best visibility.</li>
                <li className="flex items-start gap-2"><span className="text-rose-500 font-bold">6.</span> Post to LOCAL city subreddits/groups — your app is local-first, so target city by city.</li>
                <li className="flex items-start gap-2"><span className="text-rose-500 font-bold">7.</span> Nextdoor is massively underrated — perfect for "local only" dating app marketing.</li>
                <li className="flex items-start gap-2"><span className="text-rose-500 font-bold">8.</span> Respond to EVERY comment on launch day. Engagement signals boost visibility on all platforms.</li>
                <li className="flex items-start gap-2"><span className="text-rose-500 font-bold">9.</span> Cross-post TikTok videos to YouTube Shorts, Instagram Reels, and Snapchat Spotlight.</li>
                <li className="flex items-start gap-2"><span className="text-rose-500 font-bold">10.</span> Share your referral link with every early user. Each user becomes a growth channel.</li>
              </ul>
            </div>
          </TabsContent>

          {/* Platform Tabs */}
          {platforms.filter(p => p.id !== 'schedule').map((platform) => (
            <TabsContent key={platform.id} value={platform.id} className="space-y-6">
              {platform.subreddits && (
                <div className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
                  <h3 className="font-outfit font-medium text-white mb-3">Where to Post</h3>
                  <ul className="space-y-1.5">
                    {platform.subreddits.map((sub, i) => (
                      <li key={i} className="text-zinc-400 text-sm flex items-start gap-2">
                        <span className="text-rose-500 mt-0.5">•</span>
                        <span dangerouslySetInnerHTML={{ __html: sub.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') }} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {platform.groups && (
                <div className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
                  <h3 className="font-outfit font-medium text-white mb-3">Groups to Join</h3>
                  <ul className="space-y-1.5">
                    {platform.groups.map((group, i) => (
                      <li key={i} className="text-zinc-400 text-sm flex items-start gap-2">
                        <span className="text-rose-500 mt-0.5">•</span>{group}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {platform.platforms && (
                <div className="space-y-3">
                  {platform.platforms.map((p, i) => (
                    <div key={i} className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
                      <h4 className="font-medium text-white text-sm">{p.name}</h4>
                      <p className="text-zinc-500 text-xs mt-1">{p.note}</p>
                    </div>
                  ))}
                </div>
              )}

              {platform.posts && platform.posts.map((post, i) => (
                <div key={i} className="p-5 rounded-xl bg-zinc-900/50 border border-zinc-800">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      {post.title && <h3 className="font-outfit font-medium text-white text-sm mb-1">{post.title}</h3>}
                      <span className="text-zinc-600 text-xs">Post #{i + 1}</span>
                    </div>
                    <CopyButton text={post.title ? `${post.title}\n\n${post.body}` : post.body} id={`${platform.id}-${i}`} />
                  </div>
                  <div className="text-zinc-400 text-sm whitespace-pre-wrap leading-relaxed bg-zinc-950/50 rounded-lg p-4 border border-zinc-800/50">
                    {post.body}
                  </div>
                </div>
              ))}
            </TabsContent>
          ))}
        </Tabs>
      </main>

      <BottomNav active="profile" />
    </div>
  );
}
